import React, {useEffect, useState} from 'react';
import {Download, Trash2, Lock, Calendar, CheckCircle, Search, Loader2, Archive, X} from 'lucide-react';
import defaultIcon from '../../../assets/types/default.png';
import jpgIcon from '../../../assets/types/jpg.png';
import msOfficeIcon from '../../../assets/types/ms-office.png';
import pdfIcon from '../../../assets/types/pdf.png';
import pngIcon from '../../../assets/types/png.png';
import xlsxIcon from '../../../assets/types/xlsx.png';
import zipIcon from '../../../assets/types/zip.png';
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import useApi from "../../../utils/useApi";
import ClientActions from "../ClientActions/ClientActions";
import ClientDocumentPageActions from "../ClientDocumentPageActions/ClientDocumentPageActions";
import styles from "./ClientDocumentList.module.css";
import {Link, useSearchParams} from "react-router-dom";
import {useNavCount} from "../../../contexts/NavCountProvider";
import SelectTaxYear from "../SelectTaxYear/SelectTaxYear";
import {GrDocumentLocked} from "react-icons/gr";
import {IoDocumentLock} from "react-icons/io5";

const CustomAlert = ({ children }) => (
    <div className="flex items-center p-4 mb-6 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900">
        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mr-2"/>
        <div className="text-green-800 dark:text-green-200 text-sm">{children}</div>
    </div>
);

const ClientDocumentList = ({ client, documents, currentStep, loading, availableYears = [], selectedYear, onSelectYear }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [documentList, setDocumentList] = useState(documents);
    const [downloadingFiles, setDownloadingFiles] = useState({});
    const [selectedForZip, setSelectedForZip] = useState(new Set());
    const [isZipping, setIsZipping] = useState(false);
    const { deleteDocument, downloadDocument, downloadZip } = useApi();
    const [searchQuery, setSearchQuery] = useState('');
    const { refreshCounts } = useNavCount();


    useEffect(() => {
        setDocumentList(documents);
    }, [documents])

    const toggleZipSelection = (docId) => {
        setSelectedForZip(prev => {
            const newSet = new Set(prev);
            if (newSet.has(docId)) {
                newSet.delete(docId);
            } else {
                newSet.add(docId);
            }
            return newSet;
        });
    };

    const handleDownloadZip = async () => {
        if (selectedForZip.size === 0) return;

        setIsZipping(true);
        try {
            await downloadZip({
                                  client_id: client.id,
                                  docs: Array.from(selectedForZip).join(',')
                              });
            setSelectedForZip(new Set());
        } catch (error) {
            console.error('Error downloading zip:', error);
        } finally {
            setIsZipping(false);
        }
    };

    const handleDelete = async (docId, userId, fileId) => {
        const success = await deleteDocument(docId, userId, fileId);
        if (success) {
            refreshCounts();
            setDocumentList(prevDocs => {
                return prevDocs.reduce((acc, doc) => {
                    if (doc.id === docId) {
                        // Filter out the deleted file
                        const updatedFiles = doc.files.filter(file => file.id !== fileId);

                        // Only keep the document if it still has files
                        if (updatedFiles.length > 0) {
                            acc.push({
                                         ...doc,
                                         files: updatedFiles
                                     });
                        }
                    } else {
                        // Keep other documents unchanged
                        acc.push(doc);
                    }
                    return acc;
                }, []);
            });
        }
    };

    const getFileIcon = (category) => {
        switch (category?.toLowerCase()) {
            case 'png':
                return pngIcon;
            case 'pdf':
                return pdfIcon;
            case 'docx':
                return msOfficeIcon;
            case 'jpg':
            case 'jpeg':
                return jpgIcon;
            case 'zip':
                return zipIcon;
            case 'xlsx':
                return xlsxIcon;
            default:
                return defaultIcon;
        }
    };

    const formatAmount = (amount) => {
        if (!amount || amount === 0) return 'N/A';
        return `$${Number(amount).toFixed(2)}`;
    };

    const downloadFile = async (doc, file) => {
        setDownloadingFiles(prev => ({ ...prev, [file.id]: true }));
        await downloadDocument(doc, file);
        setDownloadingFiles(prev => ({ ...prev, [file.id]: false }));
    };

    // Use documentList instead of documents for filtering
    const filteredDocuments = documentList?.filter(doc =>
                                                       doc.files.some(file =>
                                                                          file?.file_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                                                          doc?.document_name?.toLowerCase().includes(searchQuery.toLowerCase()) || doc?.document_comments?.toLowerCase().includes(searchQuery.toLowerCase()),
                                                       )
    );

    if (loading) {
        return <LoadingScreen/>;
    }

    return (
        <div className="w-full page-container z-10 relative px-2">
            <div className="mb-6">
                <header className="flex row justify-between bg-white dark:bg-gray-800 shadow mb-2 rounded-lg shadow-md dark:shadow-gray-900/10 p-2 flex-wrap">
                    <div>
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                            <div className="flex flex-row justify-start items-center gap-2">
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Documents</h3>
                                <SelectTaxYear selectedYear={selectedYear}
                                               availableYears={availableYears}
                                               onSelectYear={onSelectYear}/>
                            </div>
                            <Link to={`/client-progress/${client.id}${selectedYear ? `?year=${selectedYear}` : ''}`} >
                                <h6 className="decoration-solid">{client.profile.client}</h6>
                            </Link>
                        </div>
                    </div>

                    <ClientDocumentPageActions user={client}
                                               clientId={client.id}
                                               includeProgressBtn={true}
                                               year={selectedYear}/>

                </header>
                {selectedForZip.size > 0 && (
                    <div className="mt-4 flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                        <span className="text-blue-700 dark:text-blue-300">
                            {selectedForZip.size} {selectedForZip.size === 1 ? 'document' : 'documents'} selected
                        </span>
                        <button
                            onClick={handleDownloadZip}
                            disabled={isZipping}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                        >
                            {isZipping ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                 <Archive className="w-4 h-4 mr-2" />
                             )}
                            Download Zip
                        </button>
                    </div>
                )}
                <div className="relative">

                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400 dark:text-gray-500"/>
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-gray-100"
                        placeholder="Search documents..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {currentStep && (
                <CustomAlert>
                    Your documents are being reviewed and we will be starting your tax preparation soon!
                </CustomAlert>
            )}

            {(!documentList || documentList.length === 0) && (
                <div className="text-center p-8 text-gray-500 dark:text-gray-400 page-container z-10 relative">
                    No documents found
                </div>
            )}


            {(documentList && documentList.length > 0) && (
                <div className="grid grid-cols-1 gap-4">
                    {filteredDocuments.map((doc) => (
                        doc.files.map((file) => (
                            <div
                                key={`${doc.id}-${file.id}`}
                                className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/10 p-4 transition-all hover:shadow-lg dark:hover:shadow-gray-900/20 border border-gray-100 dark:border-gray-700"
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex items-center space-x-2">
                                        <div className="flex relative">
                                            <img
                                                src={getFileIcon(file.category)}
                                                alt={file.file_name}
                                                className="w-12 h-12 object-contain"
                                            />
                                            {doc.checked == 0 && doc.admin_upload == 0 && (
                                                <div className={styles.absBadge}>
                                                    New!
                                                </div>
                                            )}
                                            {doc.locked == 1 && (
                                                <div className={styles.absIcon}>
                                                    <IoDocumentLock />
                                                </div>
                                            )}

                                        </div>

                                        <div>
                                            <h3 className="font-medium text-gray-900 dark:text-gray-100">{doc.document_name}</h3>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate" title={file.file_name}>
                                                {file.file_name}
                                            </p>
                                        </div>
                                    </div>

                                    <span className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 text-xs font-medium px-2.5 py-0.5 rounded">
                                    {formatAmount(doc.payment_amount)}
                                </span>
                                </div>

                                <div className="flex items-center gap-4 mb-4">
                                    <div className="flex-1">
                                        {doc.document_comments && (
                                            <p className="text-sm text-gray-600 dark:text-gray-300">{doc.document_comments}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                        <Calendar className="w-4 h-4 mr-1"/>
                                        {doc.created_at}
                                    </div>

                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => toggleZipSelection(doc.id)}
                                            className={`p-2 rounded-full transition-colors ${
                                                selectedForZip.has(doc.id)
                                                ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                            }`}
                                            title={selectedForZip.has(doc.id) ? "Remove from zip" : "Add to zip"}
                                        >
                                            {selectedForZip.has(doc.id) ? (
                                                <X className="w-4 h-4"/>
                                            ) : (
                                                 <Archive className="w-4 h-4"/>
                                             )}
                                        </button>
                                        {!doc.locked ? (
                                            <>
                                                <button
                                                    onClick={() => downloadFile(doc, file)}
                                                    className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-full transition-colors"
                                                    title="Download document"
                                                >
                                                    {downloadingFiles[file.id] ? (
                                                        <Loader2 className="w-4 h-4 animate-spin"/>
                                                    ) : (
                                                         <Download className="w-4 h-4"/>
                                                     )}
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(doc.id, doc.user_id, file.id)}
                                                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
                                                    title="Delete document"
                                                >
                                                    <Trash2 className="w-4 h-4"/>
                                                </button>
                                            </>
                                        ) : (
                                             <div className="flex items-center text-red-600 dark:text-red-400">
                                                 <Lock className="w-4 h-4 mr-1"/>
                                                 <span className="text-sm">Locked</span>
                                             </div>
                                         )}
                                    </div>
                                </div>
                            </div>
                        ))
                    ))}
                </div>
            )}

        </div>
    );
};

export default ClientDocumentList;
